/**
 * 以 LINE 登入 · callback landing（#24 Phase C1）· route /line-login-callback（未登入即用）。
 * backend /callback（login kind）導回此頁：帶 login_token（成功）或 err（無綁定/OAuth 失敗）。
 * login_token → 輸入 TOTP → /login-complete 發 session → saveToken → 進系統。
 */
import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { authService } from '../api/authService';
import { completeLineLogin } from '../services/lineLoginService';
import { useSystem } from '../contexts/SystemContext';
import { useIsMobile } from '../hooks/useIsMobile';

const LineLoginCallbackPage: React.FC = () => {
  const { t } = useTranslation();
  const { getMessageByCode } = useSystem();
  const isMobile = useIsMobile();   // 與 LoginPage 一致：手機/平板/PWA → PWA 行動版首頁
  const [params] = useSearchParams();
  const loginToken = params.get('login_token');
  const errKind = params.get('err');
  const account = params.get('account');   // 登入帳號（TOTP 頁顯示·挑對 authenticator entry）
  const displayName = params.get('name');
  const [totp, setTotp] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    if (!loginToken || totp.length < 6) return;
    setBusy(true); setError('');
    try {
      const res = await completeLineLogin(loginToken, totp);
      authService.saveToken(res.token);
      window.location.href = res.return_url || (isMobile ? '/ee_pwa_home' : '/home');
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      const code = typeof detail === 'string' ? detail : detail?.code;
      setError(code ? getMessageByCode(code, detail?.params) : (err?.message || t('auth.loginFailed', '登入失敗')));
    } finally { setBusy(false); }
  };

  const box: React.CSSProperties = { maxWidth: 380, margin: '80px auto', padding: 24, border: '1px solid #e0e0e0', borderRadius: 8, textAlign: 'center' };
  const btn: React.CSSProperties = { padding: '8px 18px', border: 'none', borderRadius: 4, cursor: 'pointer', color: '#fff', background: '#06c755', width: '100%' };

  // 無綁定 / OAuth 失敗
  if (!loginToken) {
    return (
      <div style={box}>
        <h3>{t('lineLogin.loginButton', '以 LINE 登入')}</h3>
        <p style={{ color: '#d32f2f' }}>
          {errKind === 'no_binding'
            ? t('lineLogin.noBindingError', '該 LINE 帳號未綁定 EE 帳號，請先帳密登入並綁定 LINE')
            : t('lineLogin.callbackError', 'LINE 授權失敗，請重試')}
        </p>
        <button style={{ ...btn, background: '#1976d2' }} onClick={() => { window.location.href = '/login'; }}>
          {t('common.back', '返回登入')}
        </button>
      </div>
    );
  }

  return (
    <div style={box}>
      <h3>{t('lineLogin.loginButton', '以 LINE 登入')}</h3>
      {account && (
        <p style={{ color: '#06c755', fontWeight: 600, marginBottom: 4 }}>
          {t('lineLogin.totpAccount', '已認證 LINE 帳號：{{account}}', {
            account: displayName ? `${account}（${displayName}）` : account,
          })}
        </p>
      )}
      <p style={{ color: '#555' }}>
        {account
          ? t('lineLogin.totpPromptNamed', '請輸入 {{account}} 的兩階段驗證碼完成登入', { account })
          : t('lineLogin.callbackTotpPrompt', '請輸入兩階段驗證碼完成登入')}</p>
      <input
        style={{ padding: '8px 10px', border: '1px solid #ccc', borderRadius: 4, width: '100%', marginBottom: 8, boxSizing: 'border-box' }}
        placeholder={t('lineLogin.totpLabel', '請輸入兩階段驗證碼')} autoFocus
        value={totp} onChange={(e) => { setTotp(e.target.value.trim()); setError(''); }}
        onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
      />
      <div style={{ fontSize: 12, color: '#999', marginBottom: 12 }}>{t('lineLogin.totpHint', '6 位驗證碼，或 8 碼備援碼')}</div>
      {error && <div style={{ color: '#d32f2f', marginBottom: 12 }}>{error}</div>}
      <button style={btn} disabled={busy || totp.length < 6} onClick={submit}>
        {busy ? t('auth.loggingIn', '登入中…') : t('lineLogin.loginButton', '以 LINE 登入')}
      </button>
    </div>
  );
};

export default LineLoginCallbackPage;
